import {
  AnimatePresence,
  motion,
  useAnimate,
  useReducedMotion,
} from 'motion/react';
import React from 'react';

import { UiTypography } from '@/components/ui/Typography';

import { AnimationFillStyles } from './AnimationFill.styles';

export const AnimationFill: React.FC<{
  onComplete: () => void;
  show: boolean;
  animate?: boolean;
  steps: {
    label: string;
    delay: number;
  }[];
  stepDuration?: number;
  progressStyles?: React.CSSProperties;
  progressBarStyles?: React.CSSProperties;
  titleToItemsSpacing?: number;
  itemsSpacing?: number;
}> = ({
  onComplete,
  show,
  animate,
  steps,
  stepDuration = 3,
  progressStyles,
  progressBarStyles,
  titleToItemsSpacing = 8,
  itemsSpacing = 24,
}) => {
  const [scope1, animate1] = useAnimate();
  const [scope2, animate2] = useAnimate();
  const [scope3, animate3] = useAnimate();

  React.useEffect(() => {
    const percantageElOne = document.querySelector(
      '.process-data-percentage-1',
    );
    const percantageElTwo = document.querySelector(
      '.process-data-percentage-2',
    );
    const percantageElThree = document.querySelector(
      '.process-data-percentage-3',
    );
    const enterAnimation = async () => {
      await animate1(
        scope1.current,
        { width: '100%' },
        {
          duration: stepDuration,
          onUpdate(value) {
            if (percantageElOne) {
              percantageElOne.textContent = `${Math.round(parseFloat(value))}%`;
            }
          },
          ease: 'easeInOut',
        },
      );
      await animate2(
        scope2.current,
        { width: '100%' },
        {
          duration: stepDuration,
          onUpdate(value) {
            if (percantageElTwo) {
              percantageElTwo.textContent = `${Math.round(parseFloat(value))}%`;
            }
          },
          ease: 'easeIn',
        },
      );
      await animate3(
        scope3.current,
        { width: '100%' },
        {
          duration: stepDuration,
          onUpdate(value) {
            if (percantageElThree) {
              percantageElThree.textContent = `${Math.round(parseFloat(value))}%`;
            }
          },
          onComplete() {
            if (onComplete) {
              onComplete();
            }
          },
          ease: 'easeOut',
        },
      );
    };
    enterAnimation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AnimationFillStyles
      className="animation-fill"
      titleToItemsSpacing={titleToItemsSpacing}
      itemsSpacing={itemsSpacing}
    >
      <ProgressItem
        label={steps[0].label}
        progressClassName="process-data-percentage-1"
        show={show}
        animate={animate}
        ref={scope1}
        delay={steps[0].delay}
        progressStyles={progressStyles}
        progressBarStyles={progressBarStyles}
      />

      <ProgressItem
        label={steps[1].label}
        progressClassName="process-data-percentage-2"
        show={show}
        animate={animate}
        ref={scope2}
        delay={steps[1].delay}
        progressStyles={progressStyles}
        progressBarStyles={progressBarStyles}
      />

      <ProgressItem
        label={steps[2].label}
        progressClassName="process-data-percentage-3"
        show={show}
        animate={animate}
        ref={scope3}
        delay={steps[2].delay}
        progressStyles={progressStyles}
        progressBarStyles={progressBarStyles}
      />
    </AnimationFillStyles>
  );
};

const ProgressItem = React.forwardRef<
  HTMLDivElement,
  {
    label: string;
    show: boolean;
    animate?: boolean;
    progressClassName: string;
    delay: number;
    progressStyles?: React.CSSProperties;
    progressBarStyles?: React.CSSProperties;
  }
>(function ProgressItem(
  {
    label,
    show,
    animate,
    progressClassName,
    delay,
    progressStyles,
    progressBarStyles,
  },
  ref,
) {
  const shouldReduceMotion = useReducedMotion();
  const canAnimate = animate && !shouldReduceMotion;

  if (canAnimate) {
    return (
      <AnimatePresence>
        {show && (
          <motion.div
            variants={{
              hidden: { opacity: 0, y: 50, transition: { delay } },
              visible: { opacity: 1, y: 0, transition: { delay } },
              exit: { opacity: 0, y: 50, transition: { delay: 0 } },
            }}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <div className="process">
              <div className="process-data">
                <UiTypography variant="body-medium">{label}</UiTypography>
                <UiTypography
                  variant="body-medium"
                  className={progressClassName}
                >
                  0%
                </UiTypography>
              </div>
              <div className="process-progress" style={progressStyles}>
                <div
                  className="process-progress-bar"
                  ref={ref}
                  style={progressBarStyles}
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  }

  return (
    <div className="process">
      <div className="process-data">
        <UiTypography variant="body-medium">{label}</UiTypography>
        <UiTypography variant="body-medium" className={progressClassName}>
          0%
        </UiTypography>
      </div>
      <div className="process-progress" style={progressStyles}>
        <div
          className="process-progress-bar"
          ref={ref}
          style={progressBarStyles}
        />
      </div>
    </div>
  );
});
